import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { useParams } from "react-router-dom";

import { useFeature } from "providers/OrganizationFeatures";
import Button from "ui/Button";
import ConnectionGraph from "features/Connections/ConnectionGraph";
import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import PageSecondaryNavigation, {
  PageNavLink,
} from "ui/PageSecondaryNavigation";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import UpgradeRequired from "ui/UpgradeRequired";
import WaitEventGraph from "features/Connections/WaitEventGraph";

import Activity from "./Activity";

import styles from "./style.module.scss";
import { useDateRange } from "providers/WithDateRange";
import DateRangeBar from "ui/DateRangeBar";
import { useRoutes } from "utils/routes";
import { usePauseAfter } from "utils/hooks";
import { ExchangeIcon } from "ui/Icons";
import { useBackendListQuery } from "./gql/Query.generated";
import { useBackendListLastActivityQuery } from "./gql/QueryLastActivity.generated";

const POLL_INTERVAL_MS = 5000; // 5 seconds
const AUTOMATIC_PAUSE_INTERVAL_MS = POLL_INTERVAL_MS * 12 * 5; // 5 minutes

function BackendList({ tab }: { tab: string }) {
  const { from, to, label } = useDateRange();
  const isLabeledRange = label != null;
  const { databaseId } = useParams();
  const [selectedTimestamp, setSelectedTimestamp] = useState(() => {
    // For labeled ranges like "last 24h", use null (meaning "now" here).
    // For explicit ranges, default to the end of the selected range.
    return isLabeledRange ? null : to.unix();
  });
  const { databaseBackends, databaseWaitEvents } = useRoutes();
  const fromTs = from.unix();
  const toTs = to.unix();
  const inRange =
    selectedTimestamp == null
      ? isLabeledRange
      : fromTs <= selectedTimestamp && selectedTimestamp <= toTs;
  useEffect(() => {
    // When navigating to a new date range:
    // - for labeled ranges ("last 24h"), clear the timestamp so we track "now"
    // - for explicit ranges, keep the current timestamp if it's still in range,
    //   otherwise fall back to the end of the new range
    setSelectedTimestamp((curr) =>
      isLabeledRange ? null : inRange ? curr : toTs,
    );
  }, [fromTs, toTs, isLabeledRange, inRange]);

  const hasActivitySnapshotsFeature = useFeature("activitySnapshots");
  const { data, loading, error } = useBackendListQuery({
    variables: { databaseId },
  });
  if ((loading && !data?.getServerDetails) || error) {
    return <Loading error={!!error} />;
  }

  const server = data.getServerDetails;
  const database = data.getDatabaseDetails;

  const handleClick = (timestamp: Date) => {
    setSelectedTimestamp(moment(timestamp).unix());
  };

  const featureNav = (
    <PageSecondaryNavigation>
      <PageNavLink to={databaseBackends(databaseId)}>
        Connection States
      </PageNavLink>
      <PageNavLink to={databaseWaitEvents(databaseId)}>Wait Events</PageNavLink>
    </PageSecondaryNavigation>
  );

  return (
    <PageContent
      title="Connections"
      pageControls={<DateRangeBar feature="Connections" />}
      windowTitle={`Connections: ${database.datname} / ${server.name}`}
      pageCategory="backends"
      pageName="index"
      featureNav={featureNav}
      pageTab={tab}
    >
      {(hasActivitySnapshotsFeature || tab == "backends") && (
        <Panel title="History">
          {tab == "backends" && (
            <ConnectionGraph databaseId={databaseId} onClick={handleClick} />
          )}
          {tab == "wait_events" && (
            <WaitEventGraph databaseId={databaseId} onClick={handleClick} />
          )}
        </Panel>
      )}
      {hasActivitySnapshotsFeature && (
        <BackendListActivity
          databaseId={databaseId}
          serverId={server.humanId}
          selectedTimestamp={selectedTimestamp}
          setSelectedTimestamp={setSelectedTimestamp}
        />
      )}
      {!hasActivitySnapshotsFeature && tab == "wait_events" && (
        <UpgradeRequired feature="Wait Event Monitoring" panelOnly />
      )}
      {!hasActivitySnapshotsFeature && tab == "backends" && (
        <UpgradeRequired feature="Connection Tracing" panelOnly />
      )}
    </PageContent>
  );
}

function BackendListLastSnapshotActivity({
  serverId,
  databaseId,
}: {
  databaseId: string;
  serverId: string;
}) {
  const [paused, setPaused] = usePauseAfter(AUTOMATIC_PAUSE_INTERVAL_MS);
  const { data, previousData, loading, error, startPolling, stopPolling } =
    useBackendListLastActivityQuery({
      variables: { serverId },
    });

  useEffect(() => {
    if (paused) {
      stopPolling();
    } else {
      startPolling(POLL_INTERVAL_MS);
    }
  }, [paused, startPolling, stopPolling]);

  const timestamp =
    data?.getServerDetails?.lastActivitySnapshotAt ??
    previousData?.getServerDetails?.lastActivitySnapshotAt;
  if ((loading && !data?.getServerDetails) || error) {
    return <Loading error={!!error} />;
  }

  const resume = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.preventDefault();
    setPaused(false);
  };

  return (
    <Panel
      title="Connection Traces"
      secondaryTitle={timestamp && moment.unix(timestamp).format("ll LTS z")}
    >
      {paused && (
        <div className={styles.activityInfo}>
          <ExchangeIcon />
          Automatic refresh paused.{" "}
          <Button onClick={resume} variant="link" className="font-bold" bare>
            Resume
          </Button>
        </div>
      )}
      {timestamp && (
        <Activity
          databaseId={databaseId}
          timestamp={timestamp}
          silentRefresh
          setPaused={setPaused}
        />
      )}
      {!timestamp && <PanelSection>No data available</PanelSection>}
    </Panel>
  );
}

function BackendListActivity({
  databaseId,
  serverId,
  selectedTimestamp,
  setSelectedTimestamp,
}: {
  databaseId: string;
  serverId: string;
  selectedTimestamp: null | number;
  setSelectedTimestamp: (value: number) => void;
}) {
  const { label } = useDateRange();
  const supportsNow = label != null;

  if (!selectedTimestamp) {
    return (
      <BackendListLastSnapshotActivity
        serverId={serverId}
        databaseId={databaseId}
      />
    );
  }

  const jumpToNow = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.preventDefault();
    setSelectedTimestamp(null);
  };

  return (
    <Panel
      title="Connection Traces"
      secondaryTitle={moment.unix(selectedTimestamp).format("ll LTS z")}
    >
      <div className={styles.activityInfo}>
        <ExchangeIcon />
        Showing historic state for{" "}
        {moment.unix(selectedTimestamp).format("ll LTS z")}.{" "}
        <Button onClick={jumpToNow} variant="link" className="font-bold" bare>
          Jump to {supportsNow ? "now" : "end of selected date range"}
        </Button>
      </div>
      <Activity
        databaseId={databaseId}
        timestamp={selectedTimestamp}
        silentRefresh={false}
      />
    </Panel>
  );
}

export default BackendList;
